import { useMemo } from "react";
import { Flex, ListItemOption, Switch, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { NullableMenu } from "@/appComponents/Blocks/Composites/ReactInputFields/DateRangeInputField/NullableMenu";
import { AgentHeadingProfileCard } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/Agent/AgentHeadingProfileCard";
import {
  AGENT_SLOTS,
  AGENT_SUPPRESSED_SLOT_IDS,
  AGENT_TOGGLEABLE_SLOT_IDS,
  AgentHeadingConfig,
  AgentSlotDef,
  AgentSlotId,
  AgentSlotGroups,
  AgentSlotIds,
  getAgentFieldOverrides,
  getAgentSettingsFromHeadingConfig,
  getAgentSlotFieldName,
  getAvailableAgentSlots,
  getAgentSlotTestSlug,
  getDefaultAgentHeadingConfig,
  getVisibleAgentSlots,
  kAgentDevShowFieldPickers
} from "@/appComponents/Blocks/Cards/ProfileBaseballCard/Agent/AgentProfile.constants";
import { getCommonMenuPropsFields } from "./ProfileHeadingConfig";
import { PROFILE_TEST_PREFIX } from "./ProfileConfiguration.utils";
import { ProfileTypes } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/ProfileBaseballCard.utils";

type AgentProfileConfigProps = {
  config: any;
  fields: string[];
  handleUpdate: (config: any, fieldChanged: string) => void;
};

/**
 * Static "Card header information" preview — placeholder copy per the mock,
 * live-reflecting the visibility toggles (mapping changes don't alter it).
 */
const AgentDummyProfileCard = ({ visibleSlots }: { visibleSlots: Set<string> }) => {
  // The heading card reads dataset FIELD names (config null ⇒ each slot's
  // default field), so the placeholder values must be keyed the same way —
  // keying by slot IDs renders "Blank"/"--" instead of the placeholder copy.
  const fieldFor = (slotId: AgentSlotId) => getAgentSlotFieldName(null, slotId);
  const placeholderRecord = {
    [fieldFor(AgentSlotIds.agentType)]: appstr("Agent type"),
    [fieldFor(AgentSlotIds.agentName)]: appstr("Name"),
    [fieldFor(AgentSlotIds.department)]: appstr("Department"),
    [fieldFor(AgentSlotIds.status)]: appstr("Status"),
    [fieldFor(AgentSlotIds.reportsTo)]: appstr("Full name (Title)"),
    [fieldFor(AgentSlotIds.createdBy)]: appstr("Full name (Title)")
  };

  return (
    <AgentHeadingProfileCard record={placeholderRecord} config={null} visibleSlots={visibleSlots} />
  );
};

export const AgentProfileConfig = ({ config, fields, handleUpdate }: AgentProfileConfigProps) => {
  const profileConfig = config?.profile || {};
  // Everything agent-related persists as ONE object at headingConfig.agent:
  // { fields: {slotId: datasetField}, shownSlots: [visible slot ids] }.
  const storedSettings = getAgentSettingsFromHeadingConfig(profileConfig?.headingConfig);

  const agentConfig: AgentHeadingConfig = useMemo(() => {
    return {
      ...getDefaultAgentHeadingConfig(),
      ...(storedSettings.fields ?? {})
    };
  }, [profileConfig]);

  const shownSlots = storedSettings.shownSlots ?? null;
  const fieldSet = useMemo(() => new Set(fields), [fields]);
  const availableSlots = useMemo(
    () => getAvailableAgentSlots(fieldName => fieldSet.has(fieldName), agentConfig),
    [fieldSet, agentConfig]
  );
  const visibleSlots = getVisibleAgentSlots(shownSlots, availableSlots);

  // Suppressed slots (INS-109) never reach the settings grid, so a group whose
  // slots are all suppressed drops out entirely rather than rendering an empty
  // header. Same single list the card reads -- see AGENT_SUPPRESSED_SLOT_IDS.
  const slotGroups = useMemo(() => {
    const groups = new Map<AgentSlotGroups, AgentSlotDef[]>();
    for (const def of AGENT_SLOTS) {
      if (AGENT_SUPPRESSED_SLOT_IDS.has(def.id)) {
        continue;
      }
      const groupSlots = groups.get(def.group) ?? [];
      groupSlots.push(def);
      groups.set(def.group, groupSlots);
    }
    return groups;
  }, []);

  const writeAgentSettings = (nextFields: AgentHeadingConfig, nextShownSlots: string[] | null) => {
    const nextProfileMap = structuredClone(profileConfig);
    if (nextProfileMap.headingConfig == null) {
      nextProfileMap.headingConfig = {};
    }
    // Persist only the DELTA from the hard-coded defaults — never a snapshot
    // of them. A stored snapshot would freeze today's column names into the
    // saved profile and win over any future defaultField update in the
    // catalog; deltas keep the defaults live-swappable.
    nextProfileMap.headingConfig[ProfileTypes.Agent] = {
      fields: getAgentFieldOverrides(nextFields),
      ...(nextShownSlots != null ? { shownSlots: nextShownSlots } : {})
    };
    handleUpdate({ profile: nextProfileMap }, "profile");
  };

  const updateMapping = (slotId: string, fieldName: string | undefined) => {
    // Clearing a mapping falls back to the slot's default field at read time.
    writeAgentSettings({ ...agentConfig, [slotId]: fieldName ?? "" }, shownSlots);
  };

  const toggleSlot = (slotId: string, nextChecked: boolean) => {
    const currentShown = shownSlots ?? [...AGENT_TOGGLEABLE_SLOT_IDS];
    const nextShown = nextChecked
      ? currentShown.includes(slotId)
        ? currentShown
        : [...currentShown, slotId]
      : currentShown.filter(id => id !== slotId);
    writeAgentSettings(agentConfig, nextShown);
  };

  // NOTE: no field-swap confirmation here (unlike employee/position heading
  // mapping) — the agent Included list holds slot IDs, not dataset field names,
  // so a mapping can never conflict with an excluded dataset field.
  const getFieldOptions = (slotId: string) => {
    const currentField = agentConfig[slotId];
    const allFields = currentField
      ? [currentField, ...fields.filter(f => f !== currentField)]
      : fields;
    return allFields.map(field => ({
      value: field,
      label: field,
      clickHandler: (option: ListItemOption) => updateMapping(slotId, option.value as string)
    }));
  };

  const renderSlotRow = (def: AgentSlotDef) => {
    const slug = getAgentSlotTestSlug(def.id);
    const isAvailable = availableSlots.has(def.id);
    const showPicker = kAgentDevShowFieldPickers && def.hasPicker;
    // Picker-only source slots (JSON alerts + the two details columns) have no
    // toggle; with the pickers gated off they would render as dead label rows,
    // so skip them entirely in the toggles-only UI.
    if (!def.hasToggle && !showPicker) {
      return null;
    }
    return (
      <Flex key={def.id} direction="column" gap="4px" className="oc-agent-profile-config-slot">
        {def.hasToggle ? (
          // Unavailable slots (field not pushed) display OFF and disabled;
          // the persisted preference is never mutated.
          <Switch
            id={`oc-agent-profile-${slug}-toggle`}
            data-testid={`${PROFILE_TEST_PREFIX}-agent-${slug}-toggle`}
            label={appstr(def.id)}
            labelPlacement="right"
            checked={visibleSlots.has(def.id)}
            disabled={!!def.locked || !isAvailable}
            onChange={() => {
              if (!def.locked && isAvailable) {
                toggleSlot(def.id, !visibleSlots.has(def.id));
              }
            }}
          />
        ) : (
          <Text className="oc-rule-label oc-font-weight-500" size="small">
            {appstr(def.id)}
          </Text>
        )}
        {showPicker && (
          <NullableMenu
            options={getFieldOptions(def.id)}
            value={agentConfig[def.id]}
            label={agentConfig[def.id]}
            nullifyValue={() => updateMapping(def.id, undefined)}
            clearButtonDataTestId={`${PROFILE_TEST_PREFIX}-agent-${slug}-clear-button`}
            {...(getCommonMenuPropsFields(`${PROFILE_TEST_PREFIX}-agent-${slug}-select`) as any)}
          />
        )}
      </Flex>
    );
  };

  return (
    <Flex gap="16px" direction="column">
      <Text modifier="bold" size="medium" element="h3">
        {appstr("Card header information")}
      </Text>
      <Flex direction="row" gap="16px">
        <Flex direction="column" gap="8px" flex="3" className="oc-agent-profile-config-dummy-card">
          <AgentDummyProfileCard visibleSlots={visibleSlots} />
        </Flex>
        <Flex flex="7" />
      </Flex>
      <div className="oc-agent-profile-config-groups">
        {Array.from(slotGroups.entries()).map(([group, slots]) => (
          <Flex
            key={group}
            direction="column"
            gap="8px"
            className="oc-agent-profile-config-group"
            dataTestId={`${PROFILE_TEST_PREFIX}-agent-group-${getAgentSlotTestSlug(group)}`}
          >
            <Text modifier="bold" size="small" element="h4">
              {appstr(group)}
            </Text>
            {slots.map(renderSlotRow)}
          </Flex>
        ))}
      </div>
    </Flex>
  );
};
